import React, { useEffect, useState, type FC, type MouseEventHandler } from 'react';

import axios, { type AxiosResponse } from 'axios';

import { Box } from '@clutter/clean';

import { Panel, Table, Button, NavLinkButton } from '@shared/components/bootstrap';
import { Picture, Pluralize } from '@shared/components/helpers';

import {
  useConfirmation,
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
} from '@admin/components/confirmation';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';

type Barcode = {
  value: string;
};

type Image = {
  id: number;
  state: string;
  source: string;
};

type Item = {
  id: number;
  hero: number;
  images: Image[];
};

type Pallet = {
  id: number;
  state: string;
  barcode: Barcode;
  items: Item[];
};

type Warehouse = {
  id: number;
  name: string;
};

type TransferPool = {
  id: number;
  pallets: Pallet[];
  origin: Warehouse;
  destination: Warehouse;
};

const useTransferRequestRemoveRequest = () => {
  const [request, setRequest] = useState<Promise<AxiosResponse> | undefined>();
  const execute = ({
    originID,
    destinationID,
    palletID,
  }: {
    originID: number;
    destinationID: number;
    palletID: number;
  }) => {
    const request = axios.delete(`/transferrequest`, {
      params: {
        origin_id: originID,
        destination_id: destinationID,
        pallet_id: palletID,
      },
    });
    setRequest(request);
    return request;
  };
  return [execute, useAxiosRequest(request)] as const;
};

const useTransferPoolPalletsRequest = (transferpoolID: number) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Pallet[]>> | undefined>();
  useEffect(() => {
    setRequest(axios.get<Pallet[]>(`/transferpools/${transferpoolID}/pallets.json`));
  }, [transferpoolID]);
  return useAxiosRequest(request);
};

const TransferPoolFormTransferRequestRemoveButton: FC<{
  transferpool: TransferPool;
  pallet: Pallet;
  onComplete(): void;
}> = ({ transferpool, pallet, onComplete }) => {
  const { confirmation, confirm } = useConfirmation();
  const [execute, { loading }] = useTransferRequestRemoveRequest();

  const onClick: MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading) return;

    const confirmed = await confirm({
      title: 'Are you sure you?',
      description: `Are you sure you want to remove the transfer request for #${pallet.barcode.value} from ${transferpool.origin.name} to ${transferpool.destination.name}?`,
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    await execute({
      originID: transferpool.origin.id,
      destinationID: transferpool.destination.id,
      palletID: pallet.id,
    });

    onComplete();
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button type="button" kind="danger" onClick={onClick} loading={loading}>
        Remove
      </Button>
    </>
  );
};

export const TransferPoolForm: FC<{
  transferpool: TransferPool;
}> = ({ transferpool }) => {
  const [removedIDs, setRemovedIDs] = useState<Set<number>>(new Set());

  const { response, loading } = useTransferPoolPalletsRequest(transferpool.id);
  const pallets = response?.data?.filter((pallet) => !removedIDs.has(pallet.id));

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>{pallets && <Pluralize count={pallets?.length} singular="Pallet" plural="Pallets" />}</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table>
          <tbody>
            {pallets?.map((pallet) => (
              <tr key={pallet.id}>
                <td className="text-left col-zero col-nowrap">
                  <div>#{pallet.barcode.value}</div>
                  <div>
                    <strong>({pallet.state})</strong>
                  </div>
                </td>
                <td className="text-left col-fill col-nowrap">
                  <Box.Flex gap="4px">
                    {pallet.items.map((item) => {
                      const hero = item.images[item.hero];
                      return (
                        <Picture
                          key={item.id}
                          source={hero && hero.state === 'uploaded' ? hero.source : undefined}
                          width={40}
                          height={40}
                        />
                      );
                    })}
                  </Box.Flex>
                </td>
                <td className="text-right col-zero col-nowrap">
                  <TransferPoolFormTransferRequestRemoveButton
                    transferpool={transferpool}
                    pallet={pallet}
                    onComplete={() => {
                      setRemovedIDs((removedIDs) => {
                        const changedIDs = new Set(removedIDs);
                        changedIDs.add(pallet.id);
                        return changedIDs;
                      });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && <Spinner />}
      </Panel.Body>
      <Panel.Footer align="right">
        <NavLinkButton kind="default" to={`/transferpools/${transferpool.id}`}>
          Cancel
        </NavLinkButton>
      </Panel.Footer>
    </Panel>
  );
};
